import { useParsedUseDate } from './useDate';

export const seasons = ['spring', 'summer', 'fall', 'winter'] as const;

export const seasonDates: Record<Season, [number, number]> = {
	spring: [2, 21],
	summer: [5, 21],
	fall: [8, 21],
	winter: [11, 21],
} as const;

export type Season = (typeof seasons)[number];

const useSeason = (): Season => {
	const parsedDate = useParsedUseDate();
	return getSeasonName(parsedDate);
};

export const getSeasonName = (date: Date): Season => {
	const month = date.getMonth();
	const day = date.getDate();

	// Return first season if no better season was found (January first and later until next season)
	let bestFittingSeason: Season = seasons[seasons.length - 1] as Season;

	// Find the best fitting season
	const seasonsInReverse = [...seasons];
	seasonsInReverse.reverse();

	for (const season of seasonsInReverse) {
		const [startMonth, startDay] = seasonDates[season];
		if (month > startMonth || (month === startMonth && day >= startDay)) {
			bestFittingSeason = season;
			break;
		}
	}

	return bestFittingSeason;
};

export const getDutchSeasonName = (season: Season): string => {
	switch (season) {
		case 'spring':
			return 'lente';
		case 'summer':
			return 'zomer';
		case 'fall':
			return 'herfst';
		case 'winter':
			return 'winter';
	}
};

export const getMonthName = (month: number): string => {
	// Month names in Dutch
	const months = [
		'januari',
		'februari',
		'maart',
		'april',
		'mei',
		'juni',
		'juli',
		'augustus',
		'september',
		'oktober',
		'november',
		'december',
	];
	return months[month] ?? '';
};

export const getNextSeason = (fromSeason: Season): Season => {
	const index = seasons.indexOf(fromSeason);
	return seasons[(index + 1) % seasons.length];
};

export const getDaysUntilNextSeason = (
	fromSeason: Season,
	currentDate: Date,
): number => {
	const nextSeason = getNextSeason(fromSeason);

	const [startMonth, startDay] = seasonDates[nextSeason];

	const currentMonth = currentDate.getMonth();
	const currentDay = currentDate.getDate();

	let daysUntilNextSeason = 0;

	// If the next season is in the same year
	if (
		currentMonth < startMonth ||
		(currentMonth === startMonth && currentDay < startDay)
	) {
		const nextSeasonDate = new Date(
			currentDate.getFullYear(),
			startMonth,
			startDay,
		);
		const difference = nextSeasonDate.getTime() - currentDate.getTime();
		daysUntilNextSeason = Math.ceil(difference / (1000 * 60 * 60 * 24));
	} else {
		// If the next season is in the next year
		const nextSeasonDate = new Date(
			currentDate.getFullYear() + 1,
			startMonth,
			startDay,
		);
		const difference = nextSeasonDate.getTime() - currentDate.getTime();
		daysUntilNextSeason = Math.ceil(difference / (1000 * 60 * 60 * 24));
	}

	return daysUntilNextSeason;
};

export default useSeason;
